import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ClipboardList, Menu, X, ChevronDown, ChevronRight, ArrowUpRight } from "lucide-react";
import { useNavigation, useSite } from "@/context/SiteDataContext";
import { useInquiryCart } from "@/context/InquiryCartContext";
import SearchOverlay from "./SearchOverlay";

function MegaProducts({ onNavigate }) {
  const { productsMega } = useNavigation();
  // Nothing is pre-selected: subcategories appear only once the visitor moves
  // onto a category, rather than the first category's list showing by default.
  const [active, setActive] = useState(null);
  const activeCat = productsMega.categories.find((c) => c.name === active) || null;
  return (
    <div className="grid grid-cols-12 min-h-[320px]">
      {/* Main categories (shown first) */}
      <div className="col-span-4 p-8">
        <p className="eyebrow mb-4">Browse by Category</p>
        <ul className="space-y-1">
          {productsMega.categories.map((c) => (
            <li key={c.name} onMouseEnter={() => setActive(c.name)} onFocus={() => setActive(c.name)}>
              <Link
                to={c.to}
                onClick={onNavigate}
                data-testid={`mega-cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center justify-between px-3.5 py-3 rounded-md font-semibold transition-all ${active === c.name ? "bg-aqua/10 text-aqua" : "text-ink hover:bg-[#f6f8f9]"}`}
              >
                {c.name}
                {c.subcategories.length > 0 && <ChevronRight className="w-4 h-4 shrink-0" />}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Subcategories — revealed only once a category is hovered */}
      <div className="col-span-5 p-8 bg-[#f9fafb] border-x border-border">
        {!activeCat ? (
          <div className="h-full flex items-center justify-center text-center">
            <p className="text-sm text-greyish max-w-[240px]">
              Hover a category to see its subcategories.
            </p>
          </div>
        ) : (
          <>
            <p className="eyebrow mb-4">
              {activeCat.subcategories.length
                ? `${activeCat.name} · Subcategories`
                : activeCat.name}
            </p>
            {activeCat.subcategories.length ? (
              <ul className="grid grid-cols-1 gap-0.5">
                {activeCat.subcategories.map((s) => (
                  <li key={s.name}>
                    <Link to={s.to} onClick={onNavigate} className="block text-sm text-greyish hover:text-aqua py-2 border-b border-border/60 last:border-0 transition-colors">
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-greyish">Browse all products in this category.</p>
            )}
            <Link to={activeCat.to} onClick={onNavigate} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-aqua hover:gap-2.5 transition-all">
              View all {activeCat.name} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </>
        )}
      </div>

      {/* Brands */}
      <div className="col-span-3 p-8">
        <p className="eyebrow mb-4">Browse by Brand</p>
        <ul className="space-y-2.5">
          {productsMega.brands.map((b) => (
            <li key={b.name}>
              <Link to={b.to} onClick={onNavigate} className="flex items-center justify-between text-sm font-semibold text-ink hover:text-aqua transition-colors group">
                {b.name} <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            </li>
          ))}
        </ul>
        <Link to="/products" onClick={onNavigate} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-aqua">
          View Full Catalogue <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function MegaServices({ onNavigate }) {
  const { servicesMega } = useNavigation();

  return (
    <div className="p-8">
      <p className="eyebrow mb-4">Our Services</p>
      <div className="grid grid-cols-3 gap-4">
        {servicesMega.map((s) => (
          <Link key={s.name} to={s.to} onClick={onNavigate} className="p-4 rounded-md border border-border hover:border-aqua/50 hover:bg-[#f6f8f9] transition-all group">
            <span className="font-bold text-ink group-hover:text-aqua transition-colors">{s.name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(null);
  const { count, openDrawer } = useInquiryCart();
  const { pathname } = useLocation();
  const { links: NAV_LINKS, productsMega, servicesMega } = useNavigation();
  const SITE = useSite();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMega(null);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled || openMega ? "bg-white/90 backdrop-blur-md border-b border-border shadow-[0_2px_20px_rgba(0,0,0,0.04)]" : "bg-white/60 backdrop-blur-sm"
        }`}
        onMouseLeave={() => setOpenMega(null)}
      >
        <div className="container-x">
          {/* Tall enough to give the enlarged logo breathing room above and below. */}
          <div className="flex items-center justify-between h-[88px]">
            <Link to="/" data-testid="header-logo" className="shrink-0">
              <img src={SITE.logos.color} alt="Athena Scientific" className="h-11 md:h-14 w-auto" />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <div key={link.label} onMouseEnter={() => setOpenMega(link.mega || null)}>
                  <NavLink
                    to={link.to}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-1 px-3.5 py-2 text-base font-semibold rounded-md transition-colors ${
                        isActive ? "text-aqua" : "text-ink hover:text-aqua"
                      }`
                    }
                  >
                    {link.label}
                    {link.mega && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMega === link.mega ? "rotate-180" : ""}`} />}
                  </NavLink>
                </div>
              ))}
            </nav>

            <div className="flex items-center gap-1">
              <button onClick={() => setSearchOpen(true)} data-testid="header-search-btn" aria-label="Search" className="w-10 h-10 flex items-center justify-center rounded-md text-ink hover:bg-[#f6f8f9] hover:text-aqua transition-all">
                <Search className="w-5 h-5" />
              </button>
              <button onClick={openDrawer} data-testid="header-cart-btn" aria-label="Inquiry Cart" className="relative w-10 h-10 flex items-center justify-center rounded-md text-ink hover:bg-[#f6f8f9] hover:text-aqua transition-all">
                <ClipboardList className="w-5 h-5" />
                <AnimatePresence>
                  {count > 0 && (
                    <motion.span
                      key={count}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 20 }}
                      data-testid="cart-badge"
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[0.62rem] font-bold text-white bg-aqua rounded-full"
                    >
                      {count}
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
              <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="Menu" className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-ink hover:bg-[#f6f8f9] transition-all">
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {openMega && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:block absolute inset-x-0 top-full bg-white border-b border-border shadow-[0_16px_40px_rgba(0,0,0,0.08)]"
            >
              <div className="container-x">
                {openMega === "products" ? <MegaProducts onNavigate={() => setOpenMega(null)} /> : <MegaServices onNavigate={() => setOpenMega(null)} />}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div className="fixed inset-0 z-[90] lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white flex flex-col"
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between h-[92px] px-5 border-b border-border">
                <img src={SITE.logos.color} alt="Athena Scientific" className="h-11 w-auto" />
                <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" className="w-10 h-10 flex items-center justify-center text-ink"><X className="w-6 h-6" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <nav className="flex flex-col">
                  {NAV_LINKS.map((link) => (
                    <div key={link.label} className="border-b border-border">
                      {link.mega ? (
                        <>
                          <button
                            onClick={() => setMobileSub(mobileSub === link.mega ? null : link.mega)}
                            className="w-full flex items-center justify-between py-3.5 font-semibold text-ink"
                          >
                            {link.label}
                            <ChevronDown className={`w-4 h-4 transition-transform ${mobileSub === link.mega ? "rotate-180" : ""}`} />
                          </button>
                          <AnimatePresence>
                            {mobileSub === link.mega && (
                              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                {/* Grouped rather than one flat list: categories
                                    and brands read as the same thing otherwise,
                                    which is confusing on a small screen. */}
                                {link.mega === "products" ? (
                                  <div className="pb-4 pl-3 flex flex-col">
                                    <Link to="/products" className="text-sm font-semibold text-ink py-1.5">
                                      All Products
                                    </Link>

                                    <p className="mt-3 mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-aqua">
                                      Categories
                                    </p>
                                    {productsMega.categories.map((c) => (
                                      <Link key={c.to} to={c.to} className="text-sm text-greyish py-1">
                                        {c.name}
                                      </Link>
                                    ))}

                                    {productsMega.brands.length > 0 && (
                                      <>
                                        <p className="mt-4 mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-aqua">
                                          Brands
                                        </p>
                                        {productsMega.brands.map((b) => (
                                          <Link key={b.to} to={b.to} className="text-sm text-greyish py-1">
                                            {b.name}
                                          </Link>
                                        ))}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div className="pb-4 pl-3 flex flex-col gap-2">
                                    {servicesMega.map((s) => (
                                      <Link key={s.to} to={s.to} className="text-sm text-greyish py-1">
                                        {s.name}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        <Link to={link.to} className="block py-3.5 font-semibold text-ink">{link.label}</Link>
                      )}
                    </div>
                  ))}
                </nav>
                <button onClick={() => { setMobileOpen(false); setSearchOpen(true); }} className="mt-6 w-full flex items-center gap-2 border border-border rounded-md px-4 py-3 text-greyish">
                  <Search className="w-5 h-5" /> Search products…
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}

